import React, { useMemo } from 'react';
import { Alert, Button, Empty, Spin, Tag, Timeline, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { formatDocumentUiDateTime } from '../../../utils/documentUiFormat';
import { useGetClosingPackageJournalQuery } from '../api/closingPackageApi';
import {
  buildClosingPackageTimelineItems,
  type ClosingPackageTimelineItem,
  type ClosingPackageTimelineResult,
} from '../lib/closingPackageTimeline';
import './closingPackageWorkspace.css';

const { Text } = Typography;

export type ClosingPackageTimelineProps = {
  applicationId: number;
};

const RESULT_TAG_COLOR: Record<ClosingPackageTimelineResult, string> = {
  success: 'success',
  rejection: 'error',
  problem: 'error',
  neutral: 'default',
};

const DOT_COLOR: Record<ClosingPackageTimelineResult, string> = {
  success: 'green',
  rejection: 'red',
  problem: 'red',
  neutral: 'blue',
};

function TimelineEventBody({ item }: { item: ClosingPackageTimelineItem }) {
  return (
    <div className="cp-timeline__event">
      <div className="cp-timeline__title-row">
        <Text strong className="cp-timeline__title">
          {item.title}
        </Text>
        <Tag color={RESULT_TAG_COLOR[item.result]} className="cp-timeline__result">
          {item.resultLabel}
        </Tag>
      </div>
      <Text type="secondary" className="cp-timeline__meta">
        {formatDocumentUiDateTime(item.timestamp)}
        {' · '}
        {item.actor}
      </Text>
      <div className="cp-timeline__chips">
        <Tag className="cp-timeline__chip">{item.stageLabel}</Tag>
        {item.cycleLabel ? <Tag className="cp-timeline__chip">{item.cycleLabel}</Tag> : null}
        {item.isLatest ? (
          <Tag color="blue" className="cp-timeline__chip">
            Последнее событие
          </Tag>
        ) : null}
      </div>
      {item.description ? (
        <Text className="cp-timeline__description">{item.description}</Text>
      ) : null}
    </div>
  );
}

/**
 * Read-only Closing Package lifecycle Timeline (FE-005A).
 * Journal remains the source of truth; this component is presentation-only.
 */
const ClosingPackageTimeline: React.FC<ClosingPackageTimelineProps> = ({ applicationId }) => {
  const { data, isLoading, isFetching, isError, refetch } = useGetClosingPackageJournalQuery(
    applicationId
  );

  const items = useMemo(
    () => buildClosingPackageTimelineItems(data?.entries),
    [data?.entries]
  );

  let body: React.ReactNode;

  if (isLoading) {
    body = (
      <div className="cp-timeline__loading">
        <Spin />
        <Text type="secondary">Загрузка истории комплекта…</Text>
      </div>
    );
  } else if (isError) {
    body = (
      <Alert
        type="error"
        showIcon
        message="Не удалось загрузить историю комплекта."
        action={
          <Button
            size="small"
            icon={<ReloadOutlined />}
            loading={isFetching}
            onClick={() => {
              void refetch();
            }}
          >
            Повторить
          </Button>
        }
      />
    );
  } else if (items.length === 0) {
    body = (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="История комплекта пока отсутствует."
      />
    );
  } else {
    body = (
      <Timeline
        className="cp-timeline__list"
        items={items.map((item) => ({
          key: item.key,
          color: DOT_COLOR[item.result],
          children: <TimelineEventBody item={item} />,
        }))}
      />
    );
  }

  return (
    <div className="cp-timeline cp-span-full" data-testid="closing-package-timeline">
      <div className="cp-timeline__header">
        <Text strong>История комплекта</Text>
        {isFetching && !isLoading ? (
          <Text type="secondary" className="cp-timeline__fetching">
            Обновление…
          </Text>
        ) : null}
      </div>
      {body}
    </div>
  );
};

export default ClosingPackageTimeline;
